import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useStatusPoll } from '@/hooks/useStatusPoll';

export interface MobileTopBarProps {
  onOpenDrawer: () => void;
  onOpenCommandPalette: () => void;
}

export function MobileTopBar({
  onOpenDrawer,
  onOpenCommandPalette,
}: MobileTopBarProps): ReactElement {
  const status = useStatusPoll();

  const dotColor = (age: number | null): string => {
    if (age === null) return 'text.disabled';
    if (age < 60) return 'success.main';
    if (age < 300) return 'warning.main';
    return 'error.main';
  };

  return (
    <Box
      component="header"
      sx={{
        height: 48,
        backgroundColor: 'background.deeper',
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
    >
      <IconButton
        size="small"
        onClick={onOpenDrawer}
        aria-label="Open navigation"
        sx={{ color: 'text.primary' }}
      >
        <MenuIcon />
      </IconButton>

      <Box
        component={RouterLink}
        to="/insights"
        sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 0.75 }}
      >
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 700,
            fontSize: 13,
            color: 'primary.main',
            textShadow: '0 0 6px rgba(217, 119, 87, 0.55)',
          }}
        >
          sherlock
        </Typography>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1}>
        <Stack direction="row" spacing={0.5}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: dotColor(status?.scheduler.age_seconds ?? null),
            }}
          />
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: dotColor(status?.dd_sync.age_seconds ?? null),
            }}
          />
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
        </Stack>
        <IconButton
          size="small"
          onClick={onOpenCommandPalette}
          aria-label="Open command palette"
          sx={{ color: 'text.faintest' }}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}
